import React, { useCallback, useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { League, FixtureStatus } from '@shootout-comps/types';

interface TeamRosterPageProps {
  leagueId: string;
  divisionId: string;
  teamId: string;
  onBack: () => void;
  onOpenPlayer: (playerId: string) => void;
  onOpenFixture: (fixtureId: string) => void;
}

// Read-only roster view reached by clicking a team on the division standings page — distinct
// from DivisionTeamsPage.tsx (the owner-facing roster *management* page with edit/add/remove
// controls), matching this codebase's established precedent of a separate read-only view rather
// than overloading the management page with a second click behaviour on the same rows.
export default function TeamRosterPage({ leagueId, divisionId, teamId, onBack, onOpenPlayer, onOpenFixture }: TeamRosterPageProps) {
  const { fetchLeague } = useLeague();
  const { t } = useLang();
  const [league, setLeague] = useState<League | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'players' | 'fixtures'>('players');

  const reload = useCallback(() => {
    fetchLeague(leagueId).then(setLeague).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [fetchLeague, leagueId]);

  useEffect(reload, [reload]);

  const division = league?.divisions.find(d => d._id === divisionId);
  const team = division?.teams.find(t => t._id === teamId);
  const fixtures = division?.fixtures.filter(f => f.homeTeamId === teamId || f.awayTeamId === teamId) ?? [];

  const STATUS_LABEL: Record<FixtureStatus, { label: string; color: string }> = {
    scheduled: { label: t.fixtureStatusScheduled, color: 'var(--chalk-dim)' },
    lineup_set: { label: t.fixtureStatusLineupSet, color: '#3498db' },
    in_progress: { label: t.fixtureStatusInProgress, color: '#2ecc71' },
    complete: { label: t.complete, color: 'var(--gold)' },
  };

  if (error && !league) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league || !division || !team) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        ← Standings
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem,5vw,2rem)', color: 'var(--gold)', marginBottom: '0.2rem' }}>
        {team.name}
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', marginBottom: '1.25rem' }}>{division.name}</p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {(['players', 'fixtures'] as const).map(key => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              background: tab === key ? 'rgba(201,168,76,0.15)' : 'transparent',
              border: `1px solid ${tab === key ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: '20px', padding: '0.4rem 1rem', fontSize: '0.8rem', fontWeight: 600,
              color: tab === key ? 'var(--gold)' : 'var(--chalk-dim)', cursor: 'pointer',
            }}
          >
            {key === 'players' ? `Players (${team.roster.length})` : `Fixtures (${fixtures.length})`}
          </button>
        ))}
      </div>

      {tab === 'players' && (
        team.roster.length === 0 ? (
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>No players yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {team.roster.map(p => (
              <div
                key={p._id}
                onClick={() => onOpenPlayer(p._id)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.85rem 1rem', cursor: 'pointer' }}
              >
                <span style={{ fontSize: '0.9rem', opacity: team.captainPlayerId === p._id ? 1 : 0.25 }}>⭐</span>
                <span style={{ flex: 1, fontSize: '0.9rem', fontWeight: 600 }}>{p.name}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)' }}>hcp {p.handicap ?? 0}</span>
                <span style={{ color: 'var(--chalk-dim)' }}>›</span>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'fixtures' && (
        fixtures.length === 0 ? (
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>No fixtures yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {fixtures.map(f => {
              const status = STATUS_LABEL[f.status];
              return (
                <div
                  key={f._id}
                  onClick={() => onOpenFixture(f._id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.85rem 1rem', cursor: 'pointer' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {f.homeTeamName} <span style={{ color: 'var(--chalk-dim)', fontWeight: 400 }}>vs</span> {f.awayTeamName}
                      {f.status === 'complete' && (
                        <span style={{ color: 'var(--gold)', fontWeight: 700, marginLeft: '0.5rem' }}>{f.homeFrameWins}–{f.awayFrameWins}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>
                      {f.scheduledAt ? new Date(f.scheduledAt).toLocaleString() : 'Not scheduled'}{f.venue ? ` · ${f.venue}` : ''}
                    </div>
                  </div>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: status.color, whiteSpace: 'nowrap' }}>{status.label}</span>
                  <span style={{ color: 'var(--chalk-dim)' }}>›</span>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
